import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { Button } from "reactstrap";
import {
  holdCart,
  clearCart,
  applyTax,
  toggleTax,
  toggleCustomerTCoins,
  retrieveCart,
  revokeDiscount,
} from "../../../ReduxToolkit/Reducers/CartReducer";
import {
  getFormattedPrice,
  getTaxRate,
  getVendorSettings,
} from "../../../services/helper";
import { toast } from "react-toastify";
import DiscountHandler from "./Discount";
import EventDiscountButton from "./EventDiscount";
import shortcutManager from "../../../services/shortcutManager";

export const CartFooter = ({ onCheckout }) => {
  const dispatch = useDispatch();
  const {
    applyTax: applyTaxState,
    tax,
    cartItems,
    total,
    discount,
    applied_coupon,
    applied_event,
    redeemPoints,
    redeemedPointsValue,
    redeemTCoins,
    redeemedTCoinsValue,
  } = useSelector((state) => state.cart);

  const taxInfo = getTaxRate();

  const subtotal = cartItems.reduce(
    (acc, item) => acc + item.price * item.quantity,
    0
  );

  const isCartEmpty = cartItems.length === 0;

  // Dispatch tax rate when applicable
  React.useEffect(() => {
    const tax_info = applyTaxState ? taxInfo : { rate: 0, label: "" };
    dispatch(applyTax(tax_info));
  }, [applyTaxState, dispatch, taxInfo]);

  const handleClearCart = () => {
    dispatch(clearCart());
  };

  const handleHoldCart = () => {
    dispatch(holdCart());
  };

  // Register cart shortcuts with centralized manager
  useEffect(() => {
    // Clear existing cart shortcuts
    shortcutManager.unregisterContext("cart");

    // Register cart shortcuts
    if (!isCartEmpty) {
      shortcutManager.register("Alt+c", () => onCheckout(), "cart");
      shortcutManager.register("Alt+t", () => handleToggleTax(), "cart");
      shortcutManager.register("Alt+h", () => handleHoldCart(), "cart");
      shortcutManager.register("Escape", () => handleClearCart(), "cart");
    }

    shortcutManager.register("Alt+n", () => handlePrintNoSale(), "cart");

    // Register Alt+1 to Alt+9 for held carts
    for (let i = 1; i <= 9; i++) {
      shortcutManager.register(
        `Alt+${i}`,
        () => {
          const cartIndex = i - 1;
          const { holdCarts } = JSON.parse(
            localStorage.getItem("cart") || '{"holdCarts":[]}'
          );
          if (holdCarts && holdCarts.length > cartIndex) {
            dispatch(retrieveCart(holdCarts[cartIndex].id));
          }
        },
        "cart"
      );
    }

    return () => {
      shortcutManager.unregisterContext("cart");
    };
  }, [dispatch, isCartEmpty, onCheckout]);

  const handlePrintNoSale = () => {
    // Fetch vendor details
    const name = getVendorSettings("name") || "N/A";
    const phone = getVendorSettings("vendor_contact_phone") || "N/A";
    const address = getVendorSettings("vendor_contact_address") || "N/A";

    // Prepare the printable content
    const printContent = `
        <div style="text-align: center; font-family: Arial, sans-serif; padding-top:15px">
          <h2>${name}</h2>
          <p><strong>Phone:</strong> ${phone}</p>
          <p><strong>Address:</strong> ${address}</p>
        </div>
      `;

    // Open a new window for printing
    const printWindow = window.open("", "", "width=500,height=400");

    if (printWindow) {
      printWindow.document.write(`
          <html>
            <head><title>Print Vendor Info</title></head>
            <body>${printContent}</body>
          </html>
        `);
      printWindow.document.close();
      printWindow.print();
    } else {
      console.error("Failed to open print window.");
    }
  };

  const handleToggleTax = () => {
    if (!taxInfo) {
      return toast.error("No tax setting found");
    }
    dispatch(toggleTax());
  };

  return (
    <div className="cart-total-section">
      <div className="cart-totals">
        <div className="cart-total-item">
          <span className="cart-total-label txt-primary">Subtotal:</span>
          <span className="cart-total-value">
            {getFormattedPrice(subtotal)}
          </span>
        </div>
        {(applyTaxState || (!applyTaxState && applied_event)) && (
          <div className="cart-total-item">
            <span className="cart-total-label txt-primary">Tax:</span>
            <span className="cart-total-value">
              {!applyTaxState && applied_event ? 'No Tax' : getFormattedPrice(tax)}
            </span>
          </div>
        )}

        {/* DISCOUNT HANDLING */}
        {!redeemPoints && !redeemTCoins && (
          discount > 0 ? (
            <div className="cart-total-item">
              <span className="cart-total-label txt-primary">Discount:</span>
              <div className="d-flex align-items-center gap-2">
                <span className="cart-total-value text-success">-{getFormattedPrice(discount)}</span>
                <Button 
                  onClick={() => dispatch(revokeDiscount())} 
                  size="sm" 
                  color="danger"
                  title="Remove discount"
                >
                  ×
                </Button>
              </div>
            </div>
          ) : (
            <DiscountHandler />
          )
        )}
        
        {/* EVENT DISCOUNT */}
        {!redeemPoints && !redeemTCoins && (
          <div className="cart-total-item">
            <EventDiscountButton />
          </div>
        )}

        {redeemPoints && (
          <div className="cart-total-item">
            <span className="cart-total-label txt-info">Redeem Points:</span>
            <span className="cart-total-value">
              {getFormattedPrice(redeemedPointsValue)}
            </span>
          </div>
        )}
        {redeemTCoins && (
          <div className="cart-total-item">
            <span className="cart-total-label txt-info">Redeem T-Coins:</span>
            <span className="cart-total-value">
              {getFormattedPrice(redeemedTCoinsValue)}
            </span>
          </div>
        )}
        <div className="cart-total-item">
          <span className="cart-total-label">Total:</span>
          <span className="cart-total-value">{getFormattedPrice(total)}</span>
        </div>
        {redeemedTCoinsValue > 0 && (
          <div className="d-flex justify-content-between">
            <Button
              color="info"
              className="btn-square w-100 mx-1"
              onClick={() => dispatch(toggleCustomerTCoins())}
            >
              {`Redeem ${redeemedTCoinsValue}T`}
            </Button>
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center px-2 py-3">
          <Button
            disabled={isCartEmpty}
            color="success"
            className="w-100"
            onClick={onCheckout}
            style={{ height: "45px" }}
          >
            Checkout <small>(Alt+C)</small>
          </Button>
        </div>

        <div className="d-flex justify-content-between">
          <div className="d-flex flex-column" style={{ width: "49%" }}>
            <Button
              disabled={isCartEmpty}
              color="primary"
              className="mb-2"
              onClick={handleHoldCart}
              title="Alt+H"
              style={{ fontSize: 14, height: "35px", width: "100%" }}
            >
              Hold <small>(Alt+H)</small>
            </Button>

            <Button
              color="info"
              onClick={handlePrintNoSale}
              title="Alt+N"
              style={{ fontSize: 14, height: "35px", width: "100%" }}
            >
              No Sale <small>(Alt+N)</small>
            </Button>
          </div>

          <div className="d-flex flex-column" style={{ width: "49%" }}>
            <Button
              disabled={isCartEmpty}
              color="success"
              className="mb-2"
              onClick={handleToggleTax}
              title="Alt+T"
              style={{ fontSize: 14, height: "35px", width: "100%" }}
            >
              Tax Overwrite <small>(Alt+T)</small>
            </Button>

            <Button
              disabled={isCartEmpty}
              color="danger"
              onClick={handleClearCart}
              title="Esc"
              style={{ fontSize: 14, height: "35px", width: "100%" }}
            >
              Clear <small>(Esc)</small>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartFooter;
